import { FC, useEffect, useState, useCallback } from 'react';
import Form from 'components/form/Form';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import API from 'core/Api';
import setServerErrors from 'utils/helpers/formErrors';
import { createErrorFromResponse } from 'core/Error';
import { usePermissionsContext } from 'store/permissions';
import { PermissionKeys } from 'store/types';
import { useModal } from 'store/modal';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { useWizardContext } from 'store/wizard';
import { useWorkModeContext } from 'store/work-mode';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { formatDate } from 'utils/helpers/formatters';
import Button from 'components/Button';
import Wizard from '../../components/Wizard';
import WizardNavAndSubmit from '../../components/WizardNavAndSubmit';
import buildWizardProps from '../../helpers';
import FileWrapper from './FileWrapper';
import HolderWrapper from './HolderWrapper';
import { IInputs } from './types';
import { IDoc } from '../types';
import Actions from './Actions';
import Notifications from './Notifications';

interface IProps {
  data: any;
}

const StepFive: FC<IProps> = ({ data }) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const { checkPermission } = usePermissionsContext();
  const { currentStep, setCurrentStep, totalSteps, setTotalSteps } = useWizardContext();
  const { workMode } = useWorkModeContext();
  const isEmployer = workMode === 'employer';
  const location = useLocation<{ relatedUsersCount?: number }>();
  const relatedUsersCount = location.state?.relatedUsersCount;

  const queryClient = useQueryClient();

  const params = useParams<{ id: string | undefined }>();

  // Flag to recognize doc mode - edit or create
  const holderId = params.id || '';

  const hasPermission = checkPermission(PermissionKeys.CAN_SELECT_DOC_WIZARD_STEP5_ACTIONS);

  const {
    documents,
    approve: approveDefaults,
    sign: signDefaults,
    notify: notifyDefaults,
    has_multi_actions: hasMultiActions,
    is_multilingual: isMultilingual,
    exit_interview_link: exitInterviewLink,
    show_exit_interview_modal: showExitInterviewModal,
    exit_interview_opened: exitInterviewOpened,
  } = data;

  const [isEditable, setIsEditable] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [scenarioMultiActions, setScenarioMultiActions] = useState<{
    [key: string]: any;
  } | null>(null);

  const isOneExternalDoc = documents.filter((doc: IDoc) => doc.type === 'external').length === 1;

  const handleEditModeChange = () => {
    setIsEditable(!isEditable);
  };

  // Build default form values
  const buildDefaultValues = () => {
    const defaultValues: any = {
      notify: notifyDefaults,
    };
    Object.keys(documents).forEach((key: string) => {
      const doc = documents[Number(key)];
      const { name, name_en: nameEn, document_id: docId, expiration_period: expPeriod, scenario, type } = doc;
      defaultValues[`doc-${docId}-name`] = name;
      if (type === 'internal' && isMultilingual) {
        defaultValues[`doc-${docId}-name-en`] = nameEn;
      }
      if (type === 'external') {
        defaultValues[`doc-${docId}-expiration_period`] = expPeriod.find((p: any) => p.selected);
        defaultValues[`doc-${docId}-scenario`] = scenario.find((p: any) => p.selected);
      }
    });
    approveDefaults?.forEach((def: any, index: number) => {
      defaultValues[`default-approve-${index + 1}`] = def;
    });
    signDefaults?.forEach((def: any, index: number) => {
      defaultValues[`default-sign-${index + 1}`] = def;
    });
    defaultValues['doc-holder-name'] = documents[0].name;
    defaultValues['doc-holder-name-en'] = documents[0].name_en;

    return defaultValues;
  };

  const methods = useForm<IInputs>({
    defaultValues: buildDefaultValues(),
  });
  const { formState, setError, reset, getValues } = methods;
  const { isSubmitting } = formState;

  useEffect(() => {
    reset(buildDefaultValues());
  }, [data]);

  useEffect(() => {
    if (isSubmitLoading) {
      showModal({
        type: 'loading',
        heading: t('docWizard.step5.loadingHeading'),
        content: t('docWizard.step5.loadingMsg'),
      });
    } else {
      hideModal();
    }
  }, [isSubmitLoading]);

  const handleSubmit = useCallback(
    (isExitInterviewVisited: boolean) => {
      setIsSubmitLoading(true);

      const submitData = getValues();
      localStorage.removeItem('formValues');

      const docsIds: number[] = [];
      const documentsData: IDoc[] = [];

      Object.keys(submitData).forEach(key => {
        const dataPairs = key.split('-');
        const fieldType = dataPairs[0];
        if (fieldType === 'doc') {
          const docId = dataPairs[1];
          docsIds.push(Number(docId));
        }
      });

      // Find unique document ids
      const uniqueIds = docsIds.filter((item, index) => docsIds.indexOf(item) === index);

      uniqueIds.forEach((id: number) => {
        // Push single document data
        documentsData.push({
          document_id: id,
          name: submitData[`doc-${id}-name`],
          ...(isMultilingual &&
            submitData[`doc-${id}-name-en`] && {
              name_en: submitData[`doc-${id}-name-en`],
            }),
          ...(!!submitData[`doc-${id}-scenario`] && {
            scenario: submitData[`doc-${id}-scenario`].value,
          }),
          ...(!!submitData[`doc-${id}-expiration_period`] && {
            expiration_period: submitData[`doc-${id}-expiration_period`].value,
          }),
          ...(!!submitData[`doc-${id}-expiration_date`] && {
            expiration_date: formatDate(submitData[`doc-${id}-expiration_date`], 'YYY-mm-dd'),
          }),
        });
      });

      const defaultDataArr = Object.keys(submitData)
        .filter(key => key.includes('default'))
        .map(key => ({
          field: key,
          value: submitData[key].value,
        }));

      const defaultActions = defaultDataArr.reduce((obj: { [key: string]: any[] }, curr) => {
        const fieldParams = curr.field.split('-');
        const objKey = fieldParams[1];
        return {
          ...obj,
          [objKey]: [...(obj[objKey] || []), curr.value],
        };
      }, {});

      const { notify } = submitData;

      const firstDocNameEn = documentsData[0].name_en ? documentsData[0].name_en : documentsData[0].name;

      return API.post(`document-wizard/step-five/${holderId}`, {
        documents: documentsData,
        document_holder_name: documents.length > 1 ? submitData['doc-holder-name'] : documentsData[0].name,
        document_holder_name_en:
          documents.length > 1 && isMultilingual ? submitData['doc-holder-name-en'] : firstDocNameEn,
        notify: notify && notify.length ? notify.map((n: any) => n.value) : [],
        approve: defaultActions.approve || [],
        sign: defaultActions.sign || [],
        exit_interview_opened: isExitInterviewVisited,
      })
        .then(res => {
          setIsSubmitLoading(false);
          setTotalSteps(5);
          setCurrentStep(1);
          // Push to next step
          const employerUrl = buildClientUrl('/documents/ready-for-sign-and-send', client);
          const employeeUrl = '/my-documents/ready-for-sign-send';
          queryClient.removeQueries(buildClientEntity('step-five', client));
          history.push(isEmployer ? employerUrl : employeeUrl);
          if (relatedUsersCount) {
            const contentMsg = t('docWizard.step5.successMsgMulti', { count: relatedUsersCount });
            const splitContent = contentMsg.split('</br>');
            const formattedContent = splitContent.join('\n');

            showModal({
              type: 'success',
              heading: t('docWizard.step5.successMsgHeading'),
              content: formattedContent,
            });
          }
        })
        .catch(res => {
          setIsSubmitLoading(false);
          const response = createErrorFromResponse(res);
          // Process errors in order to match field name with response error
          const errors: any = {};
          Object.keys(response.errors).forEach(key => {
            if (key === 'approve') {
              const approveErrGroup = response.errors[key];
              Object.keys(approveErrGroup).forEach((approveErrorsKey: any) => {
                errors[`default-approve-${Number(approveErrorsKey) + 1}`] = approveErrGroup[approveErrorsKey];
              });
            }
            if (key === 'sign') {
              const signErrGroup = response.errors[key];
              Object.keys(signErrGroup).forEach((signErrorsKey: any) => {
                errors[`default-sign-${Number(signErrorsKey) + 1}`] = signErrGroup[signErrorsKey];
              });
            }
            if (key === 'notify') errors.notify = response.errors[key];
            if (key === 'documents') {
              const docErrGroup = response.errors[key];
              Object.keys(docErrGroup).forEach((docErrorsKey: any) => {
                Object.keys(docErrGroup[docErrorsKey]).forEach((fieldErr: any) => {
                  errors[`doc-${documentsData[docErrorsKey].document_id}-${fieldErr}`] =
                    docErrGroup[docErrorsKey][fieldErr];
                });
              });
            }
          });

          setServerErrors(errors, setError);
        });
    },
    [
      client,
      documents.length,
      getValues,
      history,
      holderId,
      isEmployer,
      isMultilingual,
      queryClient,
      relatedUsersCount,
      setCurrentStep,
      setError,
      setTotalSteps,
      showModal,
      t,
    ]
  );

  const handleExitInterview = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    (isExitInterview: boolean, exitInterviewLink: string, exitInterviewOpened: boolean) => {
      if (isExitInterview && !exitInterviewOpened) {
        showModal({
          type: 'confirm',
          content: <p style={{ fontSize: '1.125rem' }}>{t('docWizard.step5.exitInterview.prompt')}</p>,
          cancelLabel: t('docWizard.step5.exitInterview.promptReject'),
          confirmButton: (
            <Button
              className="confirm-btn mb-3"
              btnType="primary"
              onClick={() => {
                hideModal();
                handleSubmit(true).then(() => {
                  setTimeout(() => {
                    window.open(exitInterviewLink, '_blank');
                  });
                });
              }}
            >
              {t('docWizard.step5.exitInterview.promptAccept')}
            </Button>
          ),
          onCancel: () => {
            handleSubmit(false);
          },
        });
        return;
      }
      handleSubmit(false);
    },
    [handleSubmit, hideModal, showModal, t]
  );

  const confirmBeforeSubmit = useCallback(() => {
    if (relatedUsersCount) {
      showModal({
        type: 'confirm',
        heading: t('docWizard.step5.confirmSubmissionHeading'),
        content: (
          <>
            <p>{t('docWizard.step5.confirmSubmissionMessage', { count: relatedUsersCount })}</p>
            <hr />
          </>
        ),
        onConfirm: () => {
          handleSubmit(false);
          hideModal();
        },
        confirmLabel: t('docWizard.step5.confirmSubmissionLabel'),
      });
    } else {
      handleExitInterview(showExitInterviewModal, exitInterviewLink, exitInterviewOpened);
    }
  }, [getValues, handleExitInterview]);

  const isExternal = data.type === 'external';

  const buildBackStep = () => {
    // When document is external, step 4 is always skipped.
    // When document is internal, the previous step is 4 as always.
    return isExternal ? 3 : 4;
  };

  const checkIfExternalDocHasMultiActions = () => {
    const multiActionFlags =
      scenarioMultiActions && Object.values(scenarioMultiActions).map(val => val.hasMultiActions);

    return multiActionFlags?.reduce((flag, curr) => {
      return flag || curr;
    }, false);
  };

  const checkIfInternalDocHasMultiActions = () => {
    const hasExternalDocsMultiActions = checkIfExternalDocHasMultiActions();
    return !isExternal && (hasMultiActions || hasExternalDocsMultiActions);
  };

  // Find the first external document index number
  const firstExternalDocIndex = documents.findIndex((doc: IDoc) => doc.type === 'external');

  return (
    <>
      <Wizard {...buildWizardProps(isExternal, currentStep, 3, totalSteps)} />
      <Row className="mt-4">
        <Col md={{ offset: 1, span: 10 }}>
          <Form
            methods={methods}
            onSubmit={confirmBeforeSubmit}
            btnLabel={t('docWizard.saveDoc')}
            isHorizontal
            hideCancelBtn
            areBtnsCentered
            showGlobalError
          >
            <WizardNavAndSubmit
              currentStep={currentStep}
              backStep={buildBackStep()}
              isLastStep
              isSubmitting={isSubmitting}
              submitLabel={t('docWizard.saveDoc')}
            />
            {documents.length > 1 && (
              <HolderWrapper
                firstDoc={documents[0]}
                methods={methods}
                isMultilingual={isMultilingual}
                isEditable={isEditable}
                handleEditModeChange={handleEditModeChange}
              />
            )}
            {documents.map((doc: IDoc, index: number) => (
              <FileWrapper
                key={`file-wrap-${doc.document_id}`}
                doc={doc}
                methods={methods}
                documents={documents}
                docHolderIsExternal={isExternal}
                isOneExternalDoc={isOneExternalDoc}
                isFirstElement={index === firstExternalDocIndex}
                setMultiActions={value => {
                  setScenarioMultiActions(scenarios => ({
                    ...scenarios,
                    ...value,
                  }));
                }}
                isMultilingual={isMultilingual}
              />
            ))}

            {(checkIfInternalDocHasMultiActions() || (isExternal && checkIfExternalDocHasMultiActions())) && (
              <>
                <Actions
                  data={data}
                  methods={methods}
                  hasPermission={hasPermission}
                />
                <hr />
              </>
            )}
            <Notifications data={data} />
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default StepFive;
